import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import CounselorChat from "@/components/CounselorChat";
import BentoPath from "@/components/BentoPath";
import LibraryCard from "@/components/LibraryCard";
import PeerLookalikeRow from "@/components/PeerLookalikeRow";
import TrendingForYou from "@/components/TrendingForYou";
// WelcomeVideoCard moved to Hub.jsx (root dashboard) per Apr 28 2026 user request.
import CPXSurveyModal from "@/components/CPXSurveyModal";
import EarnCreditsHub from "@/components/EarnCreditsHub";
import PassiveAdBanner from "@/components/PassiveAdBanner";
import { api } from "@/lib/api";
import { useT } from "@/hooks/useT";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, History, Upload, FileQuestion, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  useT(); // subscribe to global language changes
  const [searchParams, setSearchParams] = useSearchParams();
  const [credits, setCredits] = useState(0);
  const [paths, setPaths] = useState([]);
  const [activePath, setActivePath] = useState(null);
  const [history, setHistory] = useState([]);
  const [adOpen, setAdOpen] = useState(false);
  const [libraryKey, setLibraryKey] = useState(0);

  const refresh = async () => {
    try {
      const [bal, ps, hs] = await Promise.all([
        api.get("/credits/balance"),
        api.get("/counselor/paths"),
        api.get("/exam/history"),
      ]);
      setCredits(bal.data.credits);
      setPaths(ps.data || []);
      setHistory(hs.data || []);
      // Honour ?path=<id> deep-link from the hamburger "My Courses".
      // Falls back to the most recent path when the requested id is
      // missing (e.g. user followed a stale link).
      const requestedId = searchParams.get("path");
      if (!activePath) {
        if (requestedId) {
          const found = (ps.data || []).find((x) => x.path_id === requestedId);
          if (found) {
            try {
              const { data } = await api.get(`/counselor/paths/${requestedId}`);
              setActivePath(data);
            } catch {
              setActivePath(found);
            }
            // Clean the URL so subsequent navigations don't keep re-loading
            // the same path. Keeps the SPA history tidy.
            setSearchParams({}, { replace: true });
            return;
          }
        }
        if (ps.data?.length) setActivePath(ps.data[0]);
      }
    } catch (e) {
      // ignore — user feedback surfaces on specific actions
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const onNewPath = (p) => {
    setActivePath(p);
    setPaths((prev) => [p, ...prev]);
  };

  // Click on an existing path. The /counselor/paths list returns slim
  // path docs for the sidebar — re-fetch the full doc (with steps[])
  // before showing it in the bento, otherwise BentoPath renders empty.
  // Then scroll to the top so the user actually sees the change.
  const onPickPath = async (p) => {
    if (!p?.path_id) return;
    try {
      const { data } = await api.get(`/counselor/paths/${p.path_id}`);
      setActivePath(data);
    } catch (e) {
      // Fallback to whatever the sidebar already gave us — better than
      // a no-op click.
      setActivePath(p);
      toast.error("Couldn't load full course — showing summary view");
    }
    // Scroll to the top so the BentoPath card is in view. Without this,
    // users on long pages click a path and "nothing happens" because
    // the active path swap happens off-screen.
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="dashboard-page">
      <Header credits={credits} onOpenAd={() => setAdOpen(true)} />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-14 space-y-10">
        {/* WelcomeVideoCard moved to Hub.jsx (the root dashboard) per
            user request — keep the academy page focused on goal entry
            and active path generation. */}

        <CounselorChat onNewPath={onNewPath} />

        {activePath ? (
          <BentoPath
            path={activePath}
            onCreditsChange={(c) => setCredits(c)}
            onNeedCredit={() => setAdOpen(true)}
            onLibraryChange={() => setLibraryKey((k) => k + 1)}
          />
        ) : (
          <EmptyState />
        )}

        {/* Your Paths — surfaced FIRST (above Trending) per Apr 29 user
            request: "this section should come before the what's hot
            section". Click any path to swap it into the BentoPath card
            above; we scroll the page to the top so the user sees the
            change immediately. */}
        <PathsListCard paths={paths} activeId={activePath?.path_id} onPick={onPickPath} />

        {/* Peer Lookalike — surfaces 3 paths from learners with the same
            domain or seniority. Self-hides when persona is empty / no
            peers found, so it never shows an empty state. */}
        <PeerLookalikeRow />

        {/* Trending for you — aggregated hot goals over the last 14 days
            from the user's persona cohort, with global fallback when the
            cohort is too small. Self-hides when no signals are available. */}
        <TrendingForYou />

        {/* Passive AdSense banner — non-rewarded. Sits between the
            Trending row and the secondary bento so the eye flows past
            it naturally. Replaces the rewarded ad rail (now CPX surveys). */}
        <PassiveAdBanner
          slot={process.env.REACT_APP_ADSENSE_BANNER_SLOT}
          className="max-w-3xl mx-auto"
        />

        {/* Secondary bento row — paths card removed (now standalone above) */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <QuickExam onCreditsChange={setCredits} onNeedCredit={() => setAdOpen(true)} />
          <HistoryCard history={history} />
          <LibraryCard refreshKey={libraryKey} />
        </section>
      </main>

      <Footer />

      <EarnCreditsHub open={adOpen} onOpenChange={setAdOpen} onRewarded={(c) => setCredits(c)} onUpgrade={() => window.dispatchEvent(new Event("oak:open-upgrade"))} />
      <BottomNav />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="glass p-10 text-center" data-testid="empty-state">
      <div className="label-eyebrow">Your dashboard is quiet</div>
      <div className="font-display text-2xl mt-2">Generate your first learning path above.</div>
      <div className="text-white/55 text-sm mt-2">Every new user gets 6 free credits to explore adaptive exams.</div>
    </div>
  );
}

function QuickExam({ onCreditsChange, onNeedCredit }) {
  const [topic, setTopic] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!topic.trim()) { toast.error("Give the exam a topic"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("topic", topic);
      fd.append("content", content);
      if (file) fd.append("pdf", file);
      const { data } = await api.post("/exam/generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCreditsChange?.(data.credits_remaining);
      sessionStorage.setItem(`exam:${data.exam_id}`, JSON.stringify(data));
      toast.success("Exam ready");
      window.location.href = `/exam/${data.exam_id}`;
    } catch (e) {
      if (e?.response?.status === 402) { onNeedCredit?.(); }
      else toast.error(e?.response?.data?.detail || "Exam generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass p-6 flex flex-col gap-3" data-testid="quick-exam">
      <div className="flex items-center gap-2 label-eyebrow">
        <FileQuestion size={12} className="text-[#0044FF]" /> Adaptive Exam
      </div>
      <div className="font-display text-xl tracking-tight">Quiz yourself · 1 credit</div>
      <input
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="Topic (e.g. Newton's Laws)"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#0044FF]/60"
        data-testid="input-exam-topic"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        placeholder="Optional: paste notes or context"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#0044FF]/60 resize-none"
        data-testid="input-exam-content"
      />
      <label
        className="flex items-center gap-2 text-xs text-white/60 cursor-pointer hover:text-white transition px-3 py-2 rounded-lg border border-dashed border-white/15 hover:border-white/30"
        data-testid="input-exam-pdf-label"
      >
        <Upload size={12} /> {file ? file.name : "Attach PDF (optional)"}
        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="input-exam-pdf" />
      </label>
      <button
        onClick={submit}
        disabled={busy}
        className="btn-primary rounded-lg px-4 py-2.5 text-sm font-semibold font-display disabled:opacity-50"
        data-testid="btn-generate-exam"
      >
        {busy ? "Generating…" : "Generate 5 MCQs"}
      </button>
    </div>
  );
}

function HistoryCard({ history }) {
  return (
    <div className="glass p-6 flex flex-col gap-3" data-testid="history-card">
      <div className="flex items-center gap-2 label-eyebrow">
        <History size={12} className="text-[#00FF9D]" /> Recent Exams
      </div>
      {history.length === 0 ? (
        <div className="text-white/40 text-sm">No exams yet — take one above.</div>
      ) : (
        <ul className="space-y-2 text-sm">
          {history.slice(0, 6).map((h) => (
            <li key={h.exam_id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0">
              <Link to={`/exam/${h.exam_id}`} className="text-white/75 hover:text-white truncate flex-1" data-testid="history-item">
                {h.topic || "Untitled"}
              </Link>
              <span className={`text-xs font-mono-oak ${h.submitted ? "text-[#00FF9D]" : "text-white/40"}`}>
                {h.submitted ? `${h.score}%` : "in progress"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PathsListCard({ paths, activeId, onPick }) {
  return (
    <div className="glass p-6 flex flex-col gap-3" data-testid="paths-card">
      <div className="flex items-center gap-2 label-eyebrow">
        <BookOpen size={12} className="text-[#ffb067]" /> Your paths
        {paths.length > 0 && (
          <span className="ml-1 text-[10px] text-white/40 font-mono-oak tabular-nums">
            {paths.length}
          </span>
        )}
      </div>
      {paths.length === 0 ? (
        <div className="text-white/40 text-sm">No paths yet.</div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {paths.slice(0, 12).map((p) => (
            <li key={p.path_id}>
              {/* Each row navigates to the dedicated /path/:id page so
                  the user gets a real page transition (with history,
                  scroll-resume, and the Live Doubt Solver). The earlier
                  in-place state swap left users confused — clicking
                  "did nothing" because the change happened off-screen. */}
              <Link
                to={`/path/${p.path_id}`}
                onClick={() => onPick && onPick(p)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition min-w-0 ${
                  p.path_id === activeId
                    ? "bg-[#0044FF]/15 text-white border border-[#0044FF]/30"
                    : "hover:bg-white/5 text-white/75 border border-transparent"
                }`}
                data-testid="path-list-item"
                title={p.goal}
              >
                <span className="truncate flex-1 min-w-0">{p.goal}</span>
                <ArrowRight size={12} className="opacity-60 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
